import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { SportsTennis as TennisIcon, Check as CheckIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  getOrCreateUserProfile,
  getUserProfile,
  submitGroupTrainingPreference,
  clearGroupTrainingPreference,
  NTRP_LEVELS
} from '../utils/firebaseUtils';
import { NTRP_DESCRIPTIONS } from '../constants/ntrpDescriptions';

// Monday to Sunday in Lithuanian (full and short for mobile)
const WEEK_DAY_NAMES_LT = [
  'Pirmadienis',
  'Antradienis',
  'Trečiadienis',
  'Ketvirtadienis',
  'Penktadienis',
  'Šeštadienis',
  'Sekmadienis'
];
const WEEK_DAY_NAMES_SHORT = ['Pr', 'An', 'Tr', 'Kt', 'Pn', 'Št', 'Sk'];

// Hourly slots 07:00–21:00
const TIME_SLOTS = [];
for (let h = 7; h <= 21; h++) {
  TIME_SLOTS.push(`${h.toString().padStart(2, '0')}:00`);
}

const GroupTrainingPage = () => {
  const { currentUser, userProfile, setUserProfile } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  // NTRP selected on this page (editable even if already set in profile)
  const [ntrpLevel, setNtrpLevel] = useState('');
  // How many times per week (1-5)
  const [trainingsPerWeek, setTrainingsPerWeek] = useState('');
  // Set of "dayIndex-time" e.g. "0-07:00"
  const [selectedSlots, setSelectedSlots] = useState(() => new Set());
  const [ntrpDialogOpen, setNtrpDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  useEffect(() => {
    if (!currentUser || currentUser.isAnonymous) {
      setLoading(false);
      return;
    }
    const load = async () => {
      try {
        const p = await getOrCreateUserProfile(currentUser.uid, {
          email: currentUser.email || '',
          fullName: currentUser.displayName || userProfile?.fullName || '',
          phoneNumber: userProfile?.phoneNumber || ''
        });
        setProfile(p);
        setNtrpLevel(p?.ntrpLevel ?? '');
        setTrainingsPerWeek(p?.trainingsPerWeek ?? '');
        // Pre-fill from existing group preferences if any
        if (p?.groupTrainingPreferences?.length) {
          const set = new Set();
          p.groupTrainingPreferences.forEach((pref) => {
            const dayIndex = WEEK_DAY_NAMES_LT.indexOf(pref.dayName || pref.day);
            if (dayIndex >= 0 && pref.time) set.add(`${dayIndex}-${pref.time}`);
          });
          setSelectedSlots(set);
        }
      } catch (err) {
        console.error(err);
        setError('Nepavyko įkelti profilio');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentUser, userProfile?.fullName, userProfile?.phoneNumber]);

  const toggleSlot = (dayIndex, time) => {
    const key = `${dayIndex}-${time}`;
    setSelectedSlots((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Normalize preferences for comparison (order-independent)
  const normalizePreferences = (prefs) => {
    if (!prefs?.length) return [];
    const sorted = [...prefs]
      .map((p) => ({ day: p.day || p.dayName, dayName: p.dayName || p.day, time: p.time || '' }))
      .filter((p) => p.dayName && p.time)
      .sort((a, b) => (a.dayName + a.time).localeCompare(b.dayName + b.time));
    return JSON.stringify(sorted);
  };

  const canSubmit = Boolean(
    ntrpLevel &&
    trainingsPerWeek &&
    selectedSlots.size > 0
  );

  const hasPreferences = selectedSlots.size > 0 || (profile?.groupTrainingPreferences?.length ?? 0) > 0;

  const handleClearRequest = () => {
    if (!canSubmitForm) {
      setError('Norėdami pašalinti pageidavimus, prašome prisijungti.');
      return;
    }
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    setDeleteConfirmOpen(false);
    if (!canSubmitForm) return;
    setDeleting(true);
    setError('');
    setSuccess(false);
    setSuccessMessage('');
    try {
      await clearGroupTrainingPreference(currentUser.uid);
      setSelectedSlots(new Set());
      setTrainingsPerWeek('');
      const updated = await getUserProfile(currentUser.uid);
      if (updated) {
        setProfile(updated);
        setUserProfile(updated);
      }
      setSuccessMessage('Pageidavimai pašalinti.');
      setSuccess(true);
    } catch (err) {
      console.error(err);
      setError('Nepavyko pašalinti pageidavimų');
    } finally {
      setDeleting(false);
    }
  };

  const handleSubmit = async () => {
    if (!canSubmitForm) {
      setError('Norėdami pateikti pageidavimus, prašome prisijungti.');
      return;
    }
    if (!ntrpLevel) {
      setError('Pasirinkite NTRP lygį.');
      return;
    }
    if (!trainingsPerWeek) {
      setError('Pasirinkite, kiek kartų per savaitę norite treniruotis.');
      return;
    }
    if (selectedSlots.size === 0) {
      setError('Pasirinkite bent vieną dieną ir laiką kalendoriuje.');
      return;
    }
    setSubmitting(true);
    setError('');
    setSuccess(false);
    setSuccessMessage('');
    try {
      const groupTrainingPreferences = Array.from(selectedSlots).map((key) => {
        const [dayIndex, time] = key.split('-');
        const dayName = WEEK_DAY_NAMES_LT[parseInt(dayIndex, 10)];
        return { day: dayName, dayName, time };
      });
      const newNtrp = ntrpLevel ?? '';
      const newTrainingsPerWeek = trainingsPerWeek ?? '';
      const existingPrefs = profile?.groupTrainingPreferences || [];
      const existingNtrp = profile?.ntrpLevel ?? '';
      const existingTrainingsPerWeek = profile?.trainingsPerWeek ?? '';

      const isUnchanged =
        normalizePreferences(groupTrainingPreferences) === normalizePreferences(existingPrefs) &&
        newNtrp === existingNtrp &&
        newTrainingsPerWeek === existingTrainingsPerWeek;

      if (isUnchanged) {
        setSuccessMessage('Pageidavimai sėkmingai pateikti. Netrukus gausite patvirtinimą el. paštu.');
        setSuccess(true);
        setSubmitting(false);
        return;
      }

      await submitGroupTrainingPreference(currentUser.uid, {
        ntrpLevel: newNtrp,
        trainingsPerWeek: newTrainingsPerWeek,
        groupTrainingPreferences,
        fullName: profile?.fullName ?? userProfile?.fullName ?? currentUser.displayName ?? '',
        phoneNumber: profile?.phoneNumber ?? userProfile?.phoneNumber ?? '',
        email: profile?.email ?? userProfile?.email ?? currentUser.email ?? ''
      });
      const updated = await getUserProfile(currentUser.uid);
      if (updated) {
        setProfile(updated);
        setUserProfile(updated);
      }
      setSuccessMessage('Pageidavimai sėkmingai pateikti. Netrukus gausite patvirtinimą el. paštu.');
      setSuccess(true);
    } catch (err) {
      console.error(err);
      setError('Nepavyko pateikti pageidavimų');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={48} sx={{ color: 'primary.main' }} />
      </Box>
    );
  }

  // Show form but disable submission for unauthenticated users
  const canSubmitForm = currentUser && !currentUser.isAnonymous;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: { xs: 3, md: 5 } }}>
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <TennisIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1" fontWeight={700}>
            Grupinės treniruotės
          </Typography>
        </Box>

        {!canSubmitForm && (
          <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
            <Typography variant="body2">
              Galite peržiūrėti formą, bet norėdami pateikti pageidavimus grupinėms treniruotėms, prašome{' '}
              <Link to="/login" style={{ fontWeight: 600, textDecoration: 'underline' }}>
                prisijungti
              </Link>.
            </Typography>
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 3,
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Jūsų NTRP
            </Typography>
            <Typography
              component="button"
              type="button"
              onClick={() => setNtrpDialogOpen(true)}
              sx={{
                color: 'primary.main',
                textDecoration: 'underline',
                fontWeight: 600,
                fontSize: '0.875rem',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                p: 0,
                '&:hover': { color: 'primary.dark' }
              }}
              aria-label="NTRP lygių aprašymai"
            >
              Plačiau
            </Typography>
          </Box>
          <FormControl fullWidth size="small" sx={{ maxWidth: 200, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
            <InputLabel id="group-ntrp-label">NTRP lygis</InputLabel>
            <Select
              labelId="group-ntrp-label"
              label="NTRP lygis"
              value={ntrpLevel}
              onChange={(e) => setNtrpLevel(e.target.value)}
            >
              <MenuItem value="">Nepasirinkta</MenuItem>
              {NTRP_LEVELS.map((level) => (
                <MenuItem key={level} value={level}>{level}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="h6" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
            Kiek kartų per savaitę norite treniruotis?
          </Typography>
          <FormControl fullWidth size="small" sx={{ maxWidth: 120, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
            <InputLabel id="group-trainings-per-week-label">Pasirinkti</InputLabel>
            <Select
              labelId="group-trainings-per-week-label"
              label="Pasirinkti"
              value={trainingsPerWeek}
              onChange={(e) => setTrainingsPerWeek(e.target.value)}
            >
              <MenuItem value="">Nepasirinkta</MenuItem>
              {[1, 2, 3, 4, 5].map((n) => (
                <MenuItem key={n} value={String(n)}>{n}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Paper>

        <Dialog
          open={ntrpDialogOpen}
          onClose={() => setNtrpDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ fontWeight: 600 }}>NTRP lygių aprašymai</DialogTitle>
          <DialogContent dividers sx={{ maxHeight: 420 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>NTRP</Typography>
            {NTRP_DESCRIPTIONS.map((item, idx) => (
              <Box key={idx} sx={{ mb: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.level}.</Typography>
                {item.text && (
                  <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25, lineHeight: 1.5 }}>
                    {item.text}
                  </Typography>
                )}
              </Box>
            ))}
          </DialogContent>
        </Dialog>

        {/* Delete confirmation dialog */}
        <Dialog
          open={deleteConfirmOpen}
          onClose={() => setDeleteConfirmOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ fontWeight: 600 }}>Pašalinti pageidavimus?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Ar tikrai norite pašalinti visus grupinės treniruotės pageidavimus? Šis veiksmas negrįžtamas.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 0 }}>
            <Button onClick={() => setDeleteConfirmOpen(false)} sx={{ borderRadius: 2 }}>
              Atšaukti
            </Button>
            <Button
              onClick={confirmDelete}
              variant="contained"
              color="error"
              sx={{ borderRadius: 2 }}
            >
              Pašalinti
            </Button>
          </DialogActions>
        </Dialog>

        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 3,
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Kokiomis dienomis domintų grupinės treniruotės?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Pasirinkite dienas ir laikus, kurie Jums tiktų (galite pasirinkti kelis).
          </Typography>

          {/* Calendar: same layout as reservation page – fits screen, no scrollable window */}
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 0.25, sm: 2, md: 3 },
                pl: { xs: 0.5, sm: 3, md: 4 },
                maxWidth: 1000,
                width: '100%',
                overflow: 'hidden',
                borderRadius: 3,
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Box sx={{ display: 'flex', minWidth: { xs: '100%', sm: 'max-content' } }}>
                {/* Time column – compact like reservation page */}
                <Box
                  sx={{
                    position: 'sticky',
                    left: 0,
                    zIndex: 5,
                    bgcolor: 'background.paper',
                    borderRight: '1px solid',
                    borderColor: 'divider',
                    minWidth: { xs: 22, sm: 50, md: 60 },
                    flexShrink: 0
                  }}
                >
                  <Box
                    sx={{
                      height: { xs: 40, sm: 52, md: 56 },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'background.paper'
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: { xs: '0.55rem', sm: '0.875rem', md: '1rem' } }}>
                      Laikas
                    </Typography>
                  </Box>
                  {TIME_SLOTS.map((time) => (
                    <Box
                      key={time}
                      sx={{
                        height: { xs: 28, sm: 36, md: 42 },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: { xs: '0.55rem', sm: '0.75rem', md: '0.8rem' },
                        color: 'text.secondary',
                        borderBottom: '1px solid',
                        borderColor: 'divider'
                      }}
                    >
                      {time}
                    </Box>
                  ))}
                </Box>

                {/* Day columns – equal width on mobile so all 7 days fit */}
                {WEEK_DAY_NAMES_LT.map((dayName, dayIndex) => (
                  <Box
                    key={dayName}
                    sx={{
                      minWidth: { xs: 0, sm: 80, md: 100 },
                      flex: { xs: 1, sm: 'none' },
                      borderRight: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <Box
                      sx={{
                        height: { xs: 40, sm: 52, md: 56 },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'grey.50'
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        textAlign="center"
                        sx={{
                          fontSize: { xs: '0.5rem', sm: '0.75rem', md: '0.875rem' },
                          lineHeight: 1.1,
                          px: 0.5,
                          display: { xs: 'block', md: 'none' }
                        }}
                      >
                        {WEEK_DAY_NAMES_SHORT[dayIndex]}
                      </Typography>
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        textAlign="center"
                        sx={{
                          fontSize: { xs: '0.5rem', sm: '0.75rem', md: '0.875rem' },
                          lineHeight: 1.1,
                          px: 0.5,
                          display: { xs: 'none', md: 'block' }
                        }}
                      >
                        {dayName}
                      </Typography>
                    </Box>
                    {TIME_SLOTS.map((time) => {
                      const key = `${dayIndex}-${time}`;
                      const selected = selectedSlots.has(key);
                      return (
                        <Box
                          key={time}
                          onClick={() => toggleSlot(dayIndex, time)}
                          sx={{
                            height: { xs: 28, sm: 36, md: 42 },
                            bgcolor: selected ? 'primary.main' : 'background.paper',
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'background-color 0.15s ease',
                            '&:hover': {
                              bgcolor: selected ? 'primary.dark' : 'action.hover'
                            }
                          }}
                        >
                          {selected && <CheckIcon sx={{ color: 'white', fontSize: { xs: 14, sm: 18, md: 20 } }} />}
                        </Box>
                      );
                    })}
                  </Box>
                ))}
              </Box>
            </Paper>
          </Box>
        </Paper>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            size="large"
            disabled={submitting || !canSubmit || !canSubmitForm}
            onClick={handleSubmit}
            startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <CheckIcon />}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3, py: 1.5 }}
          >
            {submitting ? 'Siunčiama...' : 'Pateikti'}
          </Button>
          {hasPreferences && (
            <Button
              variant="outlined"
              color="error"
              size="large"
              disabled={submitting || deleting || !canSubmitForm}
              onClick={handleClearRequest}
              startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3, py: 1.5 }}
            >
              {deleting ? 'Pašalinama...' : 'Pašalinti'}
            </Button>
          )}
          {success && successMessage && (
            <Typography variant="body2" color="success.main" sx={{ fontWeight: 500 }}>
              {successMessage}
            </Typography>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default GroupTrainingPage;
